// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError, toDialogErrorMessage } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormShippingCarrierSearch } from '@/components/form-shipping-carrier-search';
import { FormTextField } from '@/components/form-text-field';
import { Link } from '@/components/link';
import { InfoOutlined as InfoOutlinedIcon } from '@/constants/hardfin-standard-icons';
import {
  ShippingCarrier,
  shippingCarrierSchema,
  toTrackingNumberDetailsRequest,
} from '@/constants/shipping-carrier';
import { useToast } from '@/contexts/toast-context';
import {
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { FormFulfillmentSearch } from '@/fulfillment/components/form-fulfillment-search';
import { useFulfillment } from '@/fulfillment/hooks/use-fulfillment';
import { fulfillmentsQueryKey } from '@/fulfillment/query-keys';
import { useSupportError } from '@/hooks/use-support-error';
import { useShipmentInventory } from '@/inventory/hooks/use-shipment-inventory';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { createShipment } from '@/shipment/api/create-shipment';
import { Shipment } from '@/shipment/api/types';
import {
  UpdateShipmentRequest,
  updateShipment,
} from '@/shipment/api/update-shipment';
import {
  defaultShipmentFormItem,
  mapShipmentItemsToFormItems,
  partOrProductItemSchema,
  validateShipmentItems,
} from '@/shipment/components/shipment-form-schemas';
import { ShipmentItemsTable } from '@/shipment/components/shipment-items-table';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import {
  calculateCascadeTimeSeconds,
  getTotalSerializedAssetCount,
  hasAssetEventGroupCascade,
} from '@/shipment/utilities/estimate-cascade-time';
import {
  CANCELLED_LOCK_REASON,
  MANAGED_ORDER_LOCK_REASON,
  THIRD_PARTY_ORDER_MESSAGES,
  canEditManifestInForm,
  hasLeftOpen,
} from '@/shipment/utilities/shipment-actions';
import {
  SparseFulfillment,
  SparseLocation,
  sparseLocationSchema,
} from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import { dateSchema } from '@/utilities/date-time-schema';
import { formatDuration } from '@/utilities/format-duration';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * Optional shipment to edit. If provided, the dialog will be in edit mode.
   */
  shipment?: Shipment;

  /**
   * Optional fulfillment order key for pre-populating the form in create mode.
   * When provided, the dialog will fetch the fulfillment order and populate the form.
   */
  fulfillmentOrderKey?: string | null;

  /**
   * Optional title override for the dialog. If not provided, defaults to
   * "Edit outbound shipment" when editing or "Add outbound shipment" when creating.
   */
  dialogTitle?: string;

  /**
   * Optional callback fired when the mutation in-flight state changes.
   * Used by the outer dialog wrapper to control the submit button loading state.
   */
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

// Built per shipment: the 3PL rule judges a move, so the current order passes.
const makeFormSchema = (currentFulfillmentOrderId: string | null) =>
  z
    .object({
      carrier: shippingCarrierSchema.nullable(),
      otherCarrier: z.string().optional().nullable(),
      trackingNumber: z.string().optional().nullable(),
      fulfillmentOrder: z.custom<SparseFulfillment>().nullable(),
      fromLocation: sparseLocationSchema.nullable(),
      toLocation: sparseLocationSchema.nullable(),
      expectedShippedOn: dateSchema.nullable(),
      expectedDeliveredOn: dateSchema.nullable(),
      shipmentNote: z.string().optional().nullable(),
      shippingCost: clearableCurrencyInputSchema,
      items: z
        .array(partOrProductItemSchema)
        .max(100, 'Shipments cannot have more than 100 line items'),
    })
    .superRefine((data, ctx) => {
      // Check if toLocation is set (not null)
      if (!data.toLocation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'To location is required',
          path: ['toLocation'],
        });
      }

      // Check if fromLocation is set (not null)
      if (!data.fromLocation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'From location is required',
          path: ['fromLocation'],
        });
      }

      // A 3PL creates its own shipments; only a move onto one of its orders is refused.
      if (
        data.fulfillmentOrder?.isThirdPartyFulfilled &&
        data.fulfillmentOrder.id !== currentFulfillmentOrderId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: THIRD_PARTY_ORDER_MESSAGES.fulfillmentOrder,
          path: ['fulfillmentOrder'],
        });
      }

      validateShipmentItems(data.items, ctx);

      // Additional validation for outbound shipments: require quantities
      data.items.forEach((item, index) => {
        if (item.quantity === null || item.quantity === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Quantity is required',
            path: ['items', index, 'quantity'],
          });
        } else if (item.quantity <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Quantity must be greater than 0',
            path: ['items', index, 'quantity'],
          });
        }
      });
    });

// The refinement does not change the shape, so the factory's return type is
// the form's type whatever it is built with.
type FormSchema = ReturnType<typeof makeFormSchema>;

type FormData = Omit<z.infer<FormSchema>, 'shippingCost'> & {
  shippingCost: SerializableCurrency;
};
type FormOutput = z.output<FormSchema>;

const defaultCreateValues = {
  fromLocation: null,
  toLocation: null,
  expectedShippedOn: null,
  expectedDeliveredOn: null,
  shipmentNote: null,
  items: [defaultShipmentFormItem],
  carrier: null,
  otherCarrier: null,
  trackingNumber: null,
  fulfillmentOrder: null,
};

/**
 * Data wrapper that fetches full FO data based on an FO key.
 * This component unconditionally calls hooks and should only be rendered when we have a valid key.
 */
function OutboundShipmentDialogWithData(props: {
  foKey: string;
  onDataLoaded: (fo: FulfillmentOrder) => void;
}) {
  const { foKey, onDataLoaded } = props;
  const { fulfillment } = useFulfillment(foKey);

  // Notify parent when data is loaded
  useEffect(() => {
    onDataLoaded(fulfillment);
  }, [fulfillment, onDataLoaded]);

  return null; // This component just fetches data
}

/**
 * Content component that handles form logic.
 * Watches the fulfillmentOrder form field and fetches data when an FO is selected.
 */
function OutboundShipmentDialogContent(props: Omit<Props, 'dialogTitle'>) {
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const defaultCurrencyCode = user.organization.defaultCurrency;
  const { showSupportError, hideSupportError } = useSupportError();
  const { showToast } = useToast();
  const isEditMode = !!props.shipment;
  const isShipped = !!props.shipment?.shippedAt;
  const isDelivered = !!props.shipment?.deliveredAt;
  const isCancelled = !!props.shipment?.cancelledAt;
  const isThirdPartyBound = !!props.shipment?.thirdPartyBound;
  // The destination fixes once stock lands, by either timestamp.
  const isArrived = isDelivered || !!props.shipment?.receivedAt;
  // Receipt is settable through the update door without a ship date, so each
  // milestone closes the order on its own.
  const hasShipmentLeftOpen = !!props.shipment && hasLeftOpen(props.shipment);
  const originLockReason = isCancelled
    ? CANCELLED_LOCK_REASON
    : isShipped
      ? 'From location cannot be modified after shipment has shipped'
      : undefined;
  const destinationLockReason = isCancelled
    ? CANCELLED_LOCK_REASON
    : isArrived
      ? 'To location cannot be modified after shipment has been delivered'
      : undefined;

  // Track the last FO ID we auto-populated from (for watching FO changes)
  const [lastAutoPopulatedFoId, setLastAutoPopulatedFoId] = useState<
    string | null
  >(null);

  // Store fetched FO data
  const [fullFOData, setFullFOData] = useState<FulfillmentOrder | null>(null);

  // Track previous open state to detect open transitions
  const wasOpenRef = useRef(props.isOpen);

  const getDefaultValues = useCallback(() => {
    if (isEditMode && props.shipment) {
      // Transform fulfillment order dates from DateTime objects to ISO strings for schema validation
      const fulfillmentOrder = props.shipment.fulfillmentOrder
        ? {
            ...props.shipment.fulfillmentOrder,
            promiseShipDate: props.shipment.fulfillmentOrder.promiseShipDate
              ? props.shipment.fulfillmentOrder.promiseShipDate.toISODate()
              : null,
            promiseDeliveryDate: props.shipment.fulfillmentOrder
              .promiseDeliveryDate
              ? props.shipment.fulfillmentOrder.promiseDeliveryDate.toISODate()
              : null,
          }
        : null;

      return {
        carrier: props.shipment.trackingNumberDetails?.carrier ?? null,
        otherCarrier: props.shipment.trackingNumberDetails?.name ?? null,
        trackingNumber:
          props.shipment.trackingNumberDetails?.trackingNumber ?? null,
        fulfillmentOrder: fulfillmentOrder,
        fromLocation: props.shipment.fromLocation,
        toLocation: props.shipment.toLocation,
        expectedShippedOn: props.shipment.expectedShippedOn,
        expectedDeliveredOn: props.shipment.expectedDeliveredOn,
        shipmentNote: props.shipment.shipmentNote,
        shippingCost: toCurrencyInputValue(
          props.shipment.shippingCost,
          defaultCurrencyCode
        ),
        items: mapShipmentItemsToFormItems(props.shipment.items),
      };
    }

    // In create mode, if we have a fulfillmentOrderKey, set up a minimal sparse FO
    // so the data fetching wrapper will load the full data
    if (props.fulfillmentOrderKey) {
      return {
        ...defaultCreateValues,
        shippingCost: toCurrencyInputValue(null, defaultCurrencyCode),
        fulfillmentOrder: {
          id: '',
          key: props.fulfillmentOrderKey,
          fulfillmentOrderNumber: '',
          // A stub, replaced once the full order loads below
          isThirdPartyFulfilled: false,
          to: null,
          from: null,
          items: [],
          project: null,
          promiseShipDate: null,
          promiseDeliveryDate: null,
        } as SparseFulfillment,
      };
    }

    return {
      ...defaultCreateValues,
      shippingCost: toCurrencyInputValue(null, defaultCurrencyCode),
    };
  }, [
    isEditMode,
    props.shipment,
    props.fulfillmentOrderKey,
    defaultCurrencyCode,
  ]);

  const handleClose = () => {
    setFormErrorMessage(null);
    reset({
      ...defaultCreateValues,
      shippingCost: toCurrencyInputValue(null, defaultCurrencyCode),
    });
    setLastAutoPopulatedFoId(null);
    setFullFOData(null);
    props.onClose();
  };

  const createShipmentMutation = useMutation({
    mutationFn: createShipment,
    onSuccess: (shipment) => {
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
      showToast({
        element: (
          <Typography>
            Shipment{' '}
            <Link
              to="/shipments/$shipmentKey"
              params={{ shipmentKey: shipment.key }}
              search={{ type: 'outbound' }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {shipment.shipmentNumber}
            </Link>{' '}
            created
          </Typography>
        ),
        severity: 'success',
      });
      handleClose();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      // Field-level error: highlight the offending fulfillment-order field
      // so the user can correct the selection in place.
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.invalid_fulfillment_order_number
      ) {
        methods.setError('fulfillmentOrder', {
          type: 'custom',
          message: 'Fulfillment order not found for this organization',
        });
        return;
      }
      // Reset both surfaces so a retry never leaves the prior global
      // support alert visible alongside the new inline error.
      setFormErrorMessage(null);
      hideSupportError();
      const inline = toDialogErrorMessage(apiError);
      if (inline !== null) {
        setFormErrorMessage(inline);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('An error occurred with the shipment.');
      }
    },
    onSettled: () => {
      props.onSubmittingChange?.(false);
    },
  });

  const updateShipmentMutation = useMutation({
    mutationFn: ({
      shipmentKey,
      data,
    }: {
      shipmentKey: string;
      data: UpdateShipmentRequest;
    }) => updateShipment(shipmentKey, data),
    onSuccess: () => {
      if (props.shipment) {
        queryClient.invalidateQueries({
          queryKey: shipmentDetailQueryKey(props.shipment.key),
        });
      }
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
      handleClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      // Reset both surfaces so a retry never leaves the prior global
      // support alert visible alongside the new inline error.
      setFormErrorMessage(null);
      hideSupportError();
      const inline = toDialogErrorMessage(apiError);
      if (inline !== null) {
        setFormErrorMessage(inline);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('An error occurred with the shipment.');
      }
    },
    onSettled: () => {
      props.onSubmittingChange?.(false);
    },
  });

  const currentFulfillmentOrderId =
    props.shipment?.fulfillmentOrder?.id ?? null;
  const formSchema = useMemo(
    () => makeFormSchema(currentFulfillmentOrderId),
    [currentFulfillmentOrderId]
  );

  const methods = useForm<FormData, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  });

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    trigger,
    formState: { dirtyFields },
  } = methods;

  // Watch form values for inventory checking
  const fromLocation = useWatch({
    control,
    name: 'fromLocation',
  }) as SparseLocation | null;

  // Watch fulfillment order selection - this is the single source of truth
  const selectedFulfillmentOrder = useWatch({
    control,
    name: 'fulfillmentOrder',
  });

  // Re-checked on selection so the 3PL refusal does not wait for a submit.
  const selectedFOId = selectedFulfillmentOrder?.id;
  const selectedFOIsThirdParty =
    selectedFulfillmentOrder?.isThirdPartyFulfilled;
  useEffect(() => {
    void trigger('fulfillmentOrder');
  }, [selectedFOId, selectedFOIsThirdParty, trigger]);

  const watchedCarrier = useWatch({
    control,
    name: 'carrier',
  });

  // Use inventory hook - fetches all inventory at the from location
  const {
    inventoryMap: finalInventoryMap,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
  } = useShipmentInventory({
    location: fromLocation,
  });

  // Reset form when dialog opens (transitions from closed to open) or when shipment changes
  useEffect(() => {
    const isOpening = props.isOpen && !wasOpenRef.current;
    wasOpenRef.current = props.isOpen;

    if (isOpening) {
      reset(getDefaultValues());
      // Reset auto-populate tracking so data will be re-populated
      // Don't clear fullFOData - the child fetcher will update it
      // and clearing here causes a race condition where child's effect runs first
      setLastAutoPopulatedFoId(null);
    }
  }, [props.isOpen, props.shipment, isEditMode, reset, getDefaultValues]);

  // Get the FO key from the selected FO in the form field
  const selectedFOKey = selectedFulfillmentOrder?.key;

  // Clear cached data when FO selection is cleared (so re-selecting will re-populate)
  useEffect(() => {
    if (!selectedFOKey) {
      setFullFOData(null);
      setLastAutoPopulatedFoId(null);
    }
  }, [selectedFOKey]);

  // Stable callback for data fetching wrapper
  const handleFODataLoaded = useCallback((fo: FulfillmentOrder) => {
    setFullFOData(fo);
  }, []);

  // Auto-populate form when full FO data is loaded
  useEffect(() => {
    if (isEditMode || !fullFOData) return;

    // Skip if we already populated from this FO
    if (fullFOData.id === lastAutoPopulatedFoId) return;

    if (selectedFOKey && fullFOData.key !== selectedFOKey) return;

    setValue('fulfillmentOrder', {
      id: fullFOData.id,
      key: fullFOData.key,
      fulfillmentOrderNumber: fullFOData.fulfillmentOrderNumber,
      isThirdPartyFulfilled: fullFOData.thirdParty.isEnabled,
      to: fullFOData.toLocation,
      from: fullFOData.fromLocation,
      items: fullFOData.items,
      project: fullFOData.project,
      promiseShipDate: fullFOData.promiseShipDate,
      promiseDeliveryDate: fullFOData.promiseDeliveryDate,
    });

    // Set locations from FO
    if (fullFOData.fromLocation) {
      setValue('fromLocation', fullFOData.fromLocation);
    }
    if (fullFOData.toLocation) {
      setValue('toLocation', fullFOData.toLocation);
    }

    // Convert FO items to form items format
    // FO items now have a flattened structure with embedded item properties
    const formItems = fullFOData.items.map((item) => ({
      item: extractSparseItem(item),
      quantity: null,
    }));

    setValue('items', formItems, { shouldValidate: false });

    // Mark this FO as auto-populated
    setLastAutoPopulatedFoId(fullFOData.id);
  }, [fullFOData, isEditMode, setValue, lastAutoPopulatedFoId, selectedFOKey]);

  const onSubmit = async (data: FormOutput) => {
    setFormErrorMessage(null);
    hideSupportError();
    props.onSubmittingChange?.(true);

    const items = data.items
      .filter((item) => item.item)
      .map((item) => ({
        itemId: item.item?.id || '',
        quantity: item.quantity,
      }));

    const expectedShippedOn = data.expectedShippedOn;
    const expectedDeliveredOn = data.expectedDeliveredOn;

    if (isEditMode && props.shipment) {
      const updateData: UpdateShipmentRequest = {
        expectedShippedOn: expectedShippedOn,
        expectedDeliveredOn: expectedDeliveredOn,
        shipmentNote: data.shipmentNote,
      };

      // Sending a manifest the form never showed claims a change the user did not make.
      if (canEditManifestInForm(props.shipment)) {
        updateData.items = items;
      }

      if (dirtyFields.shippingCost) {
        updateData.shippingCost = data.shippingCost ?? null;
      }

      // Only include fulfillmentOrderNumber while the shipment is still open
      if (!hasShipmentLeftOpen) {
        updateData.fulfillmentOrderNumber =
          data.fulfillmentOrder?.fulfillmentOrderNumber ?? null;
      }

      // Only include fromLocationId if the shipment hasn't been shipped
      if (!isShipped) {
        updateData.fromLocationId = data.fromLocation?.id ?? null;
      }

      // Only include toLocationId if the shipment hasn't arrived
      if (!isArrived) {
        updateData.toLocationId = data.toLocation?.id ?? null;
      }

      // Only include the carrier if not delivered
      if (!isDelivered) {
        updateData.trackingNumberDetails = toTrackingNumberDetailsRequest(
          data.carrier,
          data.otherCarrier ?? null,
          data.trackingNumber ?? null
        );
      }

      return updateShipmentMutation.mutate({
        shipmentKey: props.shipment.key,
        data: updateData,
      });
    }
    const createData = {
      shipmentType: 'outbound' as const,
      items: items,
      toLocationId: data.toLocation?.id || '',
      fulfillmentOrderNumber:
        data.fulfillmentOrder?.fulfillmentOrderNumber ?? null,
      trackingNumberDetails: toTrackingNumberDetailsRequest(
        data.carrier,
        data.otherCarrier ?? null,
        data.trackingNumber ?? null
      ),
      fromLocationId: data.fromLocation?.id,
      expectedShippedOn: expectedShippedOn,
      expectedDeliveredOn: expectedDeliveredOn,
      shipmentNote: data.shipmentNote,
      shippingCost: data.shippingCost ?? null,
    };
    return createShipmentMutation.mutate(createData);
  };

  const isOtherCarrier = watchedCarrier === ('other' as ShippingCarrier);

  const renderSectionHeader = (label: string, marginTop = 0) => (
    <Grid item xs={12}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: marginTop,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
    </Grid>
  );

  return (
    <FormProvider {...methods}>
      {/* Data fetching wrapper - only rendered when we have an FO key */}
      {selectedFOKey && (
        <Suspense fallback={null}>
          <OutboundShipmentDialogWithData
            foKey={selectedFOKey}
            onDataLoaded={handleFODataLoaded}
          />
        </Suspense>
      )}

      <form id="add-outbound-shipment-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {renderSectionHeader('Source')}

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isThirdPartyBound
                    ? MANAGED_ORDER_LOCK_REASON
                    : hasShipmentLeftOpen
                      ? 'Fulfillment order cannot be modified once the shipment is no longer open'
                      : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormFulfillmentSearch
                  name="fulfillmentOrder"
                  label="Fulfillment order"
                  control={control}
                  disabled={
                    hasShipmentLeftOpen || isCancelled || isThirdPartyBound
                  }
                />
              </Box>
            </ConditionalTooltip>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormCurrencyTextField
              control={control}
              fullWidth
              label="Shipping cost"
              name="shippingCost"
              placeholder="0.00"
            />
          </Grid>

          {renderSectionHeader('Carrier', 1)}

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isDelivered
                    ? 'Carrier cannot be modified after shipment has been delivered'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormShippingCarrierSearch
                  name="carrier"
                  label="Carrier"
                  control={control}
                  disabled={isDelivered || isCancelled}
                />
              </Box>
            </ConditionalTooltip>
            <Collapse
              in={!isOtherCarrier}
              sx={{
                marginLeft: 'auto',
                marginRight: 2,
                paddingTop: 0.5,
              }}
            >
              <Controller
                name={`carrier`}
                control={control}
                render={({ field }) => {
                  const handleClick = () => {
                    field.onChange('other');
                  };
                  return (
                    <Typography
                      role="button"
                      variant="body2"
                      color="primary.main"
                      onClick={handleClick}
                      sx={{ cursor: 'pointer', textAlign: 'right' }}
                    >
                      Enter manually
                    </Typography>
                  );
                }}
              />
            </Collapse>
            <Collapse
              in={isOtherCarrier}
              sx={{
                marginLeft: 'auto',
                paddingTop: 0.5,
              }}
            >
              <FormTextField
                fullWidth
                name={'otherCarrier'}
                label="Name"
                control={control}
                disabled={isCancelled}
              />
            </Collapse>
          </Grid>
          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isDelivered
                    ? 'Tracking number cannot be modified after shipment has been delivered'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormTextField
                  fullWidth
                  name="trackingNumber"
                  label="Tracking number"
                  control={control}
                  disabled={isDelivered || isCancelled}
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          {renderSectionHeader('Route', 1)}

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={originLockReason}
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormLocationSearch
                  name={'fromLocation'}
                  control={control}
                  label="From location"
                  grouping={['inventory']}
                  disabled={isShipped || isCancelled}
                  onCreate={(name: string) => {
                    const url = getNewLocationURL(name);
                    window.open(url, '_blank');
                  }}
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={destinationLockReason}
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormLocationSearch
                  name={'toLocation'}
                  control={control}
                  label="To location"
                  grouping={['non-inventory']}
                  disabled={isArrived || isCancelled}
                  onCreate={(name: string) => {
                    const url = getNewLocationURL(name);
                    window.open(url, '_blank');
                  }}
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          {renderSectionHeader('Timeline', 1)}

          {/* Expected ship date */}
          <Grid item xs={12} md={6}>
            <FormDatePicker
              name="expectedShippedOn"
              label="Expected ship date"
              control={control}
              monthsToShow={2}
              disabled={isCancelled}
            />
            {fullFOData?.promiseShipDate && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', display: 'block', mt: 0.5, pr: 1 }}
              >
                Promise date:{' '}
                {fullFOData.promiseShipDate.toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            )}
          </Grid>

          {/* Expected delivery date */}
          <Grid item xs={12} md={6}>
            <FormDatePicker
              name="expectedDeliveredOn"
              label="Expected delivery date"
              control={control}
              monthsToShow={2}
              disabled={isCancelled}
            />
            {fullFOData?.promiseDeliveryDate && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', display: 'block', mt: 0.5, pr: 1 }}
              >
                Promise date:{' '}
                {fullFOData.promiseDeliveryDate.toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            )}
          </Grid>

          {renderSectionHeader('Notes', 1)}

          <Grid item xs={12}>
            <FormTextField
              fullWidth
              isMultiline
              minimumRows={3}
              name="shipmentNote"
              label="Shipment notes"
              placeholder={'Optional notes about this shipment'}
              control={control}
            />
          </Grid>

          {/* Shipment items - only while the manifest is still editable */}
          {(!props.shipment || canEditManifestInForm(props.shipment)) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <ShipmentItemsTable
                name="items"
                isShipped={isShipped}
                fromLocation={fromLocation}
                inventoryMap={finalInventoryMap}
                isLoadingInventory={isLoadingInventory}
                isFetchingInventory={isFetchingInventory}
                includeFulfillmentDetails={!!fullFOData}
                fulfillmentItems={fullFOData?.items || []}
              />
            </Grid>
          )}
        </Grid>
      </form>
      {formErrorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {formErrorMessage}
        </Alert>
      )}
    </FormProvider>
  );
}

/**
 * Dialog for adding a new outbound shipment or editing an existing one.
 * Fetches all necessary data internally based on the fulfillmentOrderKey prop.
 */
export function OutboundShipmentDialog(props: Props) {
  const isEditMode = !!props.shipment;

  // Track whether a mutation is in-flight so the submit button can show a loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compute whether editing will cascade to asset event groups
  const willCascade =
    isEditMode && props.shipment
      ? hasAssetEventGroupCascade(props.shipment, 'outbound')
      : false;

  // Calculate serialized asset count and estimated cascade time
  const serializedAssetCount =
    isEditMode && props.shipment
      ? getTotalSerializedAssetCount(props.shipment.items)
      : 0;
  const estimatedCascadeSeconds =
    calculateCascadeTimeSeconds(serializedAssetCount);

  return (
    <Dialog
      key={props.shipment?.key} // Force remount when shipment changes
      fullWidth
      maxWidth="lg"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
    >
      <DialogTitle>
        {props.dialogTitle ??
          (isEditMode ? 'Edit outbound shipment' : 'Add outbound shipment')}
      </DialogTitle>
      <DialogContent dividers>
        <Suspense
          fallback={
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
            >
              <CircularProgress />
            </Box>
          }
        >
          <OutboundShipmentDialogContent
            {...props}
            onSubmittingChange={setIsSubmitting}
          />
        </Suspense>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {/* Cascade warning - shown when editing will cascade to AEGs */}
        <Box display="flex" alignItems="center">
          {willCascade && serializedAssetCount > 0 && (
            <Alert
              icon={<InfoOutlinedIcon fontSize="small" />}
              severity="info"
              sx={{
                py: 0.5,
                px: 1.5,
                alignItems: 'center',
                '& .MuiAlert-message': { py: 0 },
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1 }}>
                Editing this shipment will update{' '}
                {pluralize(serializedAssetCount, { singular: 'asset' })}
              </Typography>
            </Alert>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {/* Estimated time - shown immediately left of Cancel */}
          {willCascade && estimatedCascadeSeconds > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              Estimated time: {formatDuration(estimatedCascadeSeconds)}
            </Typography>
          )}
          <Button onClick={props.onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-outbound-shipment-form"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={16} /> : undefined
            }
          >
            {isSubmitting
              ? 'Updating...'
              : isEditMode
                ? 'Edit shipment'
                : 'Create shipment'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
